import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SortEvent } from 'primeng/api';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { sortAscFactory, sortDesFactory } from '@shared/services/sorting.service';

import { CommandSet } from './commands/commands.model';
import { ExpandableResource, leafCounter, sortCollectionWith, treeContainerExtractor } from './expandable-resource';

const ensureIsDistinctSorting = (
  { field: fieldA, order: orderA }: SortEvent,
  { field: fieldB, order: orderB }: SortEvent = { field: '', order: -1 }
) => {
  return fieldA !== fieldB || orderA !== orderB;
};

@UntilDestroy()
@Component({
  selector: 'miq-tree-data-table',
  templateUrl: './tree-data-table.component.html',
  styleUrls: ['./tree-data-table.component.scss'],
  standalone: false,
})
export class TreeDataTableComponent<T = any> implements OnInit, OnDestroy {
  @Input() title: string;
  @Input() resources: Array<ExpandableResource<T>> = [];
  @Input() columns: any[];
  @Input() dataKey = 'id';
  @Input() commands: CommandSet[];
  @Input() isDownloadReportList = false;

  @Output() rowChange = new EventEmitter<T>();
  @Output() selectedRows = new EventEmitter<Array<ExpandableResource<T>>>();
  @Output() sortChange = new EventEmitter<SortEvent>(null);

  selectedResources = [];
  elements = [];

  pageSize = 0;
  selectedRowsPerPage = 10;
  defaultSorting = '';
  defaultSortOrder = 1;

  private lastSortEvent: SortEvent;
  private sortingFn;
  private dataSource = new BehaviorSubject<Array<ExpandableResource<T>>>([]);
  private treeElements = this.dataSource.asObservable();

  ngOnInit() {
    this.dataSource.next(this.resources);
    this.treeElements
      .pipe(
        untilDestroyed(this),
        map(elements => treeContainerExtractor(elements))
      )
      .subscribe((elements: Array<ExpandableResource<T>>) => {
        this.elements = elements;
        this.pageSize = leafCounter(this.dataSource.getValue() || []);
      });
    const deafultSortColum = this.columns.find(({ defaultSort }) => !!defaultSort);
    this.defaultSorting = deafultSortColum ? deafultSortColum.field : '';
    this.defaultSortOrder = deafultSortColum ? deafultSortColum.sortOrder : 1;
  }

  ngOnDestroy() {
    /** REQUIRED FOR UNTILDESTROY */
  }

  toggle(element) {
    element.toggle();
    this.dataSource.next(this.dataSource.value);
  }

  sort($event: SortEvent) {
    if (ensureIsDistinctSorting($event, this.lastSortEvent)) {
      this.lastSortEvent = $event;
      const sortingFactory = $event.order === 1 ? sortAscFactory : sortDesFactory;
      this.sortingFn = sortingFactory($event.field);
      const { folders, reports } = this.foldersAlwaysOnTop(this.resources);
      const sortedFolders = sortCollectionWith(folders, this.sortingFn).sort(sortAscFactory('name'));
      const sortedResources =
        $event.field !== 'name'
          ? [...sortedFolders, ...sortCollectionWith(reports, this.sortingFn)]
          : sortCollectionWith(this.resources, this.sortingFn);
      this.dataSource.next(sortedResources);
      this.sortChange.emit($event);
    }
  }

  onSelectionChange($event) {
    this.selectedResources = $event;
    this.selectedRows.emit(this.selectedResources);
  }

  foldersAlwaysOnTop(resources: Array<ExpandableResource<T>>): {
    folders: Array<ExpandableResource<T>>;
    reports: Array<ExpandableResource<T>>;
  } {
    return resources.reduce(
      (results, report) => {
        const { folders, reports } = results;
        if (report.isDir) {
          folders.push(report);
        } else {
          reports.push(report);
        }
        return results;
      },
      { folders: [], reports: [] }
    );
  }
}
